import React, { useState } from "react";
import axios from "../api/axios";
import { LOGIN_URL } from "../api/url";
import { useAuthContext } from "../hooks/useAuthContext";
import { useValidContext } from "../hooks/useValidContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import { getErrorMessage } from "../utils/errorMessage";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { dispatch } = useAuthContext();
  const { validator } = useValidContext();

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const { notifySuccess, notifyError, notifyLoading, notifyDismiss } = useToast();

  const login = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const toastId = notifyLoading("Signing you in...");

    try {
      const usernameNoSpace = username.replace(/\s/g, "");
      const data = { username: usernameNoSpace, password };

      const response = await axios.post(LOGIN_URL, data);
      const json = response.data;
      const valtoken = json.valToken;

      // ❌ Error from backend
      if (json?.error) {
        notifyDismiss(toastId);
        notifyError(json.error);
        setSubmitting(false);
        return;
      }

      // ✅ Store token
      if (!valtoken) {
        localStorage.setItem("accessToken", JSON.stringify(json));
        dispatch({ type: "LOGIN", payload: json });
      } else {
        localStorage.setItem(
          "accessToken",
          JSON.stringify({ token: json.token, username: json.username })
        );

        dispatch({ type: "LOGIN", payload: json });

        localStorage.setItem("validToken", JSON.stringify(valtoken));
        validator({ type: "VALIDATE", payload: valtoken });
      }

      notifyDismiss(toastId);
      notifySuccess("Welcome back! Redirecting...");

      setTimeout(() => {
        navigate(from, { replace: true });
        window.location.reload();
      }, 800);
    } catch (err) {
      notifyError(getErrorMessage(err, "Couldn't connect to server. Please try again."));
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-5">
            {/* Header */}
            <div className="text-center mb-4">
              <h1 className="header-title mb-1">Sign In</h1>
              <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                Login to continue to your dashboard.
              </p>
            </div>

            {/* Card */}
            <div className="card ds-card shadow-sm">
              <div className="card-body p-4">
                <form onSubmit={login}>
                  {/* Username */}
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control shadow-none"
                      id="floatingUsername"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      required
                    />
                    <label htmlFor="floatingUsername">Username</label>
                  </div>

                  {/* Password */}
                  <div className="form-floating mb-3 position-relative">
                    <input
                      type={showPass ? "text" : "password"}
                      className="form-control shadow-none"
                      id="floatingPassword"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      style={{ paddingRight: "3rem" }}
                    />
                    <label htmlFor="floatingPassword">Password</label>

                    {/* Eye Toggle */}
                    <button
                      type="button"
                      className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                      style={{ border: "none" }}
                      onClick={() => setShowPass((v) => !v)}
                      tabIndex={-1}
                    >
                      <i
                        className={`bi ${
                          showPass ? "bi-eye-slash" : "bi-eye"
                        }`}
                        style={{ fontSize: "1.1rem" }}
                      />
                    </button>
                  </div>

                  {/* Forgot */}
                  <div className="d-flex justify-content-end mb-3">
                    <Link
                      to="/forgot-password"
                      className="text-decoration-none"
                      style={{ fontSize: 13 }}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {/* Submit */}
                  <div className="d-grid">
                    <button
                      className="btn btn-dark py-2"
                      type="submit"
                      disabled={submitting}
                      style={{ borderRadius: 14 }}
                    >
                      {submitting ? "Signing in..." : "Sign In"}
                    </button>
                  </div>
                </form>

                <hr className="my-4" />

                <p
                  className="text-center text-muted mb-0"
                  style={{ fontSize: 13 }}
                >
                  Secure login powered by RTM Program.
                </p>
              </div>
            </div>

            {/* Footer tip */}
            <p
              className="text-center text-muted mt-3"
              style={{ fontSize: 12 }}
            >
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
